import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { PawPrintIcon as PawPrint, ArrowRightIcon as ArrowRight, WarningIcon as Warning, CheckCircleIcon as CheckCircle, XIcon as X } from "@phosphor-icons/react";

/**
 * Admin-only bulk reassignment for dogs uploaded to the wrong account.
 *
 * Use case (founder, 2026-05-28): "I uploaded all 18 of my dogs to my
 * admin profile instead of my user profile. Move them over."
 *
 * Flow:
 *   1. Page mounts → list every dog where created_by == calling admin
 *   2. Admin types destination email + selects dogs to move
 *   3. Confirm modal → POST /admin/dogs/reassign-bulk
 *   4. Per-dog result strip (moved / skipped + reason)
 *
 * Safety: backend validates the destination user exists + only moves
 * dogs whose CURRENT created_by matches the admin's id. Each move
 * appends an `ownership_history` entry with reason='admin_self_correction'
 * so the audit trail isn't broken.
 */
export default function AdminDogReassign() {
  const [dogs, setDogs] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [destEmail, setDestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    api.get("/admin/dogs/owned-by-me").then((r) => {
      setDogs(r.data?.dogs || []);
    }).catch((e) => {
      toast.error(formatApiError(e));
      setDogs([]);
    });
  };
  useEffect(load, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set((dogs || []).map((d) => d.id)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dogs/reassign-bulk", {
        dog_ids: Array.from(selected),
        destination_email: destEmail.trim(),
      });
      setResult(data);
      setConfirming(false);
      toast.success(`Moved ${data.moved_count} of ${selected.size} dogs.`);
      load();
      clearAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!dogs) return <AppShell><div className="max-w-4xl mx-auto px-4 py-10">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-dog-reassign">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-block">
            ← Back to admin
          </Link>
          <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight">Bulk reassign dogs</h1>
          <p className="text-[13px] sm:text-sm text-[#6B6B6B] mt-1 max-w-2xl">
            Move dogs currently owned by the admin account to a real user
            account. Each move appends an audit-trail entry; related
            litters, contracts, and breeding listings reattach
            automatically. Dogs owned by anyone else are silently skipped.
          </p>
        </header>

        {dogs.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-[#EAEAEA] p-8 text-center" data-testid="admin-reassign-empty">
            <CheckCircle size={28} weight="duotone" className="text-[#0F8043] mx-auto mb-2" />
            <h3 className="font-spectral text-lg">No dogs to reassign</h3>
            <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-md mx-auto">
              You don't own any dogs on the admin account.
            </p>
          </div>
        ) : (
          <>
            {/* Destination + bulk controls */}
            <div className="rounded-2xl border border-[#EAEAEA] bg-white p-4 sm:p-5 mb-5">
              <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#6B6B6B] block mb-1">
                Destination user email
              </label>
              <Input
                value={destEmail}
                onChange={(e) => setDestEmail(e.target.value)}
                placeholder="lauren@example.com"
                type="email"
                data-testid="admin-reassign-dest-email"
                className="font-tabular"
              />
              <p className="text-[11px] text-[#6B6B6B] mt-2">
                The user must already exist. If they haven't registered yet, ask them
                to create an account first.
              </p>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <p className="text-[13px] text-ink">
                <strong>{selected.size}</strong> of {dogs.length} selected
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAll} className="rounded-full" data-testid="admin-reassign-select-all">
                  Select all
                </Button>
                {selected.size > 0 && (
                  <Button size="sm" variant="outline" onClick={clearAll} className="rounded-full" data-testid="admin-reassign-clear">
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <ul className="space-y-2 mb-6" data-testid="admin-reassign-list">
              {dogs.map((d) => {
                const isSel = selected.has(d.id);
                return (
                  <li
                    key={d.id}
                    onClick={() => toggle(d.id)}
                    className={`bg-white border-2 rounded-xl p-3 sm:p-4 flex items-center gap-3 cursor-pointer transition-colors ${
                      isSel ? "border-crimson" : "border-[#EAEAEA] hover:border-ink/30"
                    }`}
                    data-testid={`admin-reassign-dog-${d.id}`}
                  >
                    <Checkbox checked={isSel} onCheckedChange={() => toggle(d.id)} />
                    <span className="p-2 rounded-full bg-[#FBF7EC]">
                      <PawPrint size={16} weight="duotone" className="text-gold-dark" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-ink truncate">
                        {d.name || "(unnamed)"}
                        {d.registration_number && (
                          <span className="text-[11px] text-[#6B6B6B] font-normal ml-2">{d.registration_number}</span>
                        )}
                      </p>
                      <p className="text-[11px] text-[#6B6B6B] truncate">
                        {d.breed || "—"}
                        {d.dob && <> · DOB {new Date(d.dob).toLocaleDateString()}</>}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="sticky bottom-4 bg-white border-2 border-ink rounded-2xl p-4 shadow-lg flex items-center justify-between flex-wrap gap-3">
              <div className="text-[12.5px] min-w-0">
                {selected.size === 0
                  ? <span className="text-[#6B6B6B]">Select dogs to move</span>
                  : <span><strong>{selected.size}</strong> dog{selected.size === 1 ? "" : "s"} → <strong className="text-crimson">{destEmail || "(no destination)"}</strong></span>}
              </div>
              <Button
                onClick={() => setConfirming(true)}
                disabled={selected.size === 0 || !destEmail.trim() || busy}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="admin-reassign-submit-btn"
              >
                Reassign selected <ArrowRight size={13} weight="bold" className="ml-1" />
              </Button>
            </div>
          </>
        )}

        {confirming && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-reassign-confirm-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-3">
                <Warning size={22} weight="fill" className="text-crimson mt-0.5" />
                <h3 className="font-spectral text-xl tracking-tight">Confirm reassignment</h3>
              </div>
              <p className="text-sm text-[#3F3F3F] mb-4">
                Move <strong>{selected.size}</strong> dog{selected.size === 1 ? "" : "s"} from your admin account to{" "}
                <strong>{destEmail}</strong>?
              </p>
              <p className="text-[12px] text-[#6B6B6B] mb-4">
                Each dog will get an audit-trail entry with reason
                <code className="text-[11px] mx-1">admin_self_correction</code>.
                This cannot be auto-reversed; the recipient would need to
                transfer them back through the normal flow.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy} className="rounded-full">
                  Cancel
                </Button>
                <Button onClick={submit} disabled={busy} className="rounded-full bg-crimson hover:bg-ink text-white" data-testid="admin-reassign-confirm-btn">
                  {busy ? "Moving…" : "Yes, reassign"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {result && (
          <section className="mt-6 rounded-2xl border border-[#BFE8C9] bg-[#EEFBEC] p-5" data-testid="admin-reassign-result">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle size={20} weight="fill" className="text-[#0F8043] mt-0.5" />
              <div>
                <h3 className="font-spectral text-lg tracking-tight">Done</h3>
                <p className="text-[13px] text-[#3F3F3F]">
                  Moved <strong>{result.moved_count}</strong>{" "}
                  {result.skipped_count > 0 && <>· skipped <strong>{result.skipped_count}</strong></>}
                </p>
              </div>
            </div>
            {result.moved.length > 0 && (
              <ul className="space-y-1 text-[12.5px] mb-3">
                {result.moved.map((m) => (
                  <li key={m.dog_id} className="flex items-center gap-2 text-[#0F8043]">
                    <CheckCircle size={12} weight="fill" /> {m.dog_name || m.dog_id}
                  </li>
                ))}
              </ul>
            )}
            {result.skipped.length > 0 && (
              <details className="text-[12px]">
                <summary className="text-[#6B6B6B] cursor-pointer hover:text-ink">
                  Skipped ({result.skipped.length})
                </summary>
                <ul className="mt-1 space-y-1">
                  {result.skipped.map((s) => (
                    <li key={s.dog_id} className="flex items-start gap-2 text-[#6B6B6B]">
                      <X size={12} weight="bold" className="mt-0.5 shrink-0" />
                      <span>{s.dog_name || s.dog_id} — {s.reason}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
